import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useRef } from "react";
import { FaTimes } from "react-icons/fa";
import { useClinicToDoctorRequestActionAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useClinicToDoctorRequestActionAPI";
import { AHS_CLICK_EVENTS, useAhsInventoryClickLog } from "@/app/hooks/useAhsInventoryClickLog";

interface ICancelRequestModalProps {
  open?: boolean;
  setOpen?: (e: boolean) => void;
  selectedRequestId?: string;
  clinicId?: string;
  onCancelDone?: () => void;
}

export default function CancelRequestModal({ open, selectedRequestId, clinicId, setOpen, onCancelDone }: ICancelRequestModalProps) {
  const cancelButtonRef = useRef(null);
  const { execute: cancelRequest, isSubmitting: isCancelling } = useClinicToDoctorRequestActionAPI();
  const { logAhsEvent } = useAhsInventoryClickLog();

  const handleClose = () => {
    if (!isCancelling) setOpen(false);
  };

  const handleCancelRequest = async () => {
    if (!selectedRequestId || !clinicId) return;
    logAhsEvent(AHS_CLICK_EVENTS.CANCEL_REQUEST_CONFIRM_CLICK, {
      event_action: "cancel",
      request_id: selectedRequestId,
    });
    const { status } = await cancelRequest(selectedRequestId, "cancel", {
      clinic_id: clinicId,
      reason: "Cancelled by user",
    });
    if (status) {
      setOpen(false);
      onCancelDone?.();
    }
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-2 sm:p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all w-full sm:my-4 sm:max-w-[480px] sm:p-6 flex flex-col">
                  <button onClick={handleClose} className="absolute top-4 right-3 sm:top-6 text-gray-500 hover:text-gray-700">
                    <FaTimes size={16} />
                  </button>
                  <div className="pb-2 pr-6">
                    <div className="font-semibold text-sm">Are you sure you want to cancel?</div>
                  </div>
                  <div className="flex justify-end gap-2 mt-6">
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-sm font-medium cursor-pointer hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={isCancelling}
                      onClick={handleCancelRequest}
                      className="px-4 py-2 rounded-md bg-[#193A6A] text-white text-sm font-medium cursor-pointer disabled:opacity-50 hover:bg-[#14305a]"
                    >
                      {isCancelling ? "Processing..." : "Proceed"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
